'use client';

import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/components/ui/use-toast';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { getIsAnonymous } from '@/lib/LocalStorage';
import { appendCampaignParamsToUrl } from '@/lib/persistCampaignParams';
import { ProfileTab } from '@/lib/ProfileTab';
import { Requester } from '@/lib/Requester';
import { appRPC } from '@/lib/utils';
import { WebLink } from '@/lib/WebLinks';
import { GenericResponse } from '@/models/GenericResponse';
import { Order, OrderResponse } from '@/models/Order';
import { zodResolver } from '@hookform/resolvers/zod';
import { Package } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormMessage } from './ui/form';

const OrderDetailDialog = dynamic(
  () => import('./OrderDetailDialog').then((mod) => ({ default: mod.OrderDetailDialog })),
  { ssr: false },
);

const emailOrPhoneRegex = new RegExp(/^(?:\d{10}|\w+@\w+\.\w{2,3})$/);

export default function OrderTrackView({
  isSidebar = false,
  isApp = false,
  afterPurchase = false,
  initialPopoverOpen = false,
}: {
  isSidebar?: boolean;
  isApp?: boolean;
  afterPurchase?: boolean;
  initialPopoverOpen?: boolean;
}) {
  const [popoverOpen, setPopoverOpen] = useState<boolean>(initialPopoverOpen);
  const [activeOrder, setActiveOrder] = useState<Order | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const { toast } = useToast();
  const router = useRouter();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const FormSchema = z.object({
    orderNumber: z.string().min(3, {
      message: 'Lütfen sipariş numarasını giriniz.',
    }),
    userKey: z
      .string()
      .regex(emailOrPhoneRegex, 'Geçerli bir e-posta adresi veya telefon numarası giriniz'),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      orderNumber: '',
      userKey: '',
    },
  });

  async function onSubmit(values: z.infer<typeof FormSchema>) {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      const params = {
        orderNumber: values.orderNumber,
        userKey: values.userKey,
      };

      const { data: result } = await Requester.get<GenericResponse<OrderResponse>>(
        Endpoints.GET_ORDER_BY_NUMBER,
        { params },
      );

      if (result.data.order) {
        setActiveOrder(result.data.order);
        form.reset();
      } else {
        toast({
          variant: 'destructive',
          description: 'Girilen bilgiler ile bir sipariş bulunamadı',
        });
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function onTrackOrderClick() {
    if (getIsAnonymous()) {
      setPopoverOpen(true);
      return;
    }

    const targetUrl = appendCampaignParamsToUrl(`${WebLink.PROFILE}?type=${ProfileTab.ORDERS}`);

    if (isApp) {
      const RPCcheck = appRPC('route', 'navigateWithUrl', { url: targetUrl, target: 'user' });
      if (!RPCcheck && window) {
        window.location.href = targetUrl;
        return;
      } else if (RPCcheck) {
        return;
      }
    }

    router.push(targetUrl);
  }

  return (
    <>
      {isMobile ? (
        <Drawer
          open={popoverOpen}
          onOpenChange={(open) => {
            if (!open) {
              setPopoverOpen(open);
            } else {
              onTrackOrderClick();
            }
          }}
        >
          {afterPurchase ? (
            <span className="font-semibold cursor-pointer" onClick={onTrackOrderClick}>
              Sipariş Takibi
            </span>
          ) : (
            <DrawerTrigger asChild>
              {isSidebar ? (
                <Button className="flex items-center space-x-2 px-2 h-[38px] rounded-full bg-[#5E6AFF1A] hover:bg-[#5E6AFF1A] transition-all duration-300 w-1/2 focus-visible:ring-0 focus-visible:ring-offset-0">
                  <Package className="size-5 text-white" />
                  <span className="text-[12px] font-medium text-white line-clamp-1">
                    Sipariş Takibi
                  </span>
                </Button>
              ) : (
                <Button
                  className="flex rounded-full h-8 w-[42px] p-0 justify-center items-center hover:cursor-pointer bg-[#5E6AFF1A] hover:bg-hzc_purple_400 transition-all hover:scale-105 duration-300 text-hzc_beige_100"
                  aria-label="Siparişlerim"
                  onClick={onTrackOrderClick}
                >
                  <Package className="size-6" />
                </Button>
              )}
            </DrawerTrigger>
          )}
          <DrawerContent className="bg-[#2B3280] border-none">
            <div className="mx-auto w-full max-w-sm p-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none text-white">Siparişim Nerede?</h4>
                  <p className="text-sm text-muted-foreground text-white">
                    Vermiş olduğunuz siparişi aşağıdaki kısa formu doldurarak takip edebilirsiniz.
                  </p>
                </div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
                    <FormField
                      control={form.control}
                      name="orderNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Sipariş Numarası"
                              className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="userKey"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="E-Posta Adresi veya Telefon numarası"
                              className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                              {...field}
                              autoCapitalize="none"
                              autoCorrect="off"
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="bg-[#D2F25C] w-full hover:bg-[#D2F25C] text-[#2B3280]"
                    >
                      Sorgula
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover
          open={popoverOpen}
          onOpenChange={(open) => {
            if (!open) {
              setPopoverOpen(open);
            } else {
              onTrackOrderClick();
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              className="hidden sm:flex rounded-full h-12  justify-center items-center hover:cursor-pointer bg-[#5E6AFF1A] hover:bg-hzc_purple_400 transition-all hover:scale-105 duration-300 text-hzc_beige_100"
              aria-label="Sipariş Takibi"
              onClick={onTrackOrderClick}
            >
              <span className="py-2 text-sm font-medium">Sipariş Takibi</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none text-hzc_beige_900">Siparişim Nerede?</h4>
                <p className="text-sm text-muted-foreground text-hzc_beige_900">
                  Vermiş olduğunuz siparişi aşağıdaki kısa formu doldurarak takip edebilirsiniz.
                </p>
              </div>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
                  <FormField
                    control={form.control}
                    name="orderNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="Sipariş Numarası"
                            className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="userKey"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="E-Posta Adresi veya Telefon numarası"
                            className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                            {...field}
                            autoCapitalize="none"
                            autoCorrect="off"
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="bg-hzc_purple_500 w-full hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300"
                  >
                    Sorgula
                  </Button>
                </form>
              </Form>
            </div>
          </PopoverContent>
        </Popover>
      )}

      <OrderDetailDialog activeOrder={activeOrder} setActiveOrder={setActiveOrder} />
    </>
  );
}
